import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
    AlertCircle,
    BarChart3,
    ChevronDown,
    ChevronUp,
    Code2,
    FileText,
    Globe,
    Gauge,
    ImagePlus,
    LayoutTemplate,
    Package,
    PanelLeftClose,
    PanelLeftOpen,
    Pencil,
    Plus,
    RefreshCw,
    Save,
    ShieldCheck,
    ShoppingCart,
    Tags,
    Trash2,
    Upload,
    Wallet,
    X,
} from 'lucide-react';
import { API_BASE_URL } from '../../config';
import {
    appendAtPath,
    createFaqItem,
    createSeoFaqItem,
    createSeoHighlight,
    createSeoSection,
    createSeoStep,
    readNumber,
    readString,
    readStringList,
    removeAtPath,
    stringListToText,
    textToStringList,
    updateAtPath,
} from './siteContentHelpers';

const SECTION_GROUPS = [
    {
        label: 'Сайт',
        items: [
            { id: 'seoPages', label: 'Страницы сайта', icon: LayoutTemplate },
            { id: 'stats', label: 'Показатели', icon: BarChart3 },
        ],
    },
    {
        label: 'Продажи и каталог',
        items: [
            { id: 'purchase', label: 'Оформление заказа', icon: ShoppingCart },
            { id: 'digitalGoods', label: 'Цифровые товары', icon: Package },
            { id: 'pricing', label: 'Цены витрины', icon: Wallet },
        ],
    },
    {
        label: 'Поиск и интеграции',
        items: [
            { id: 'seo', label: 'Общее SEO', icon: Globe },
            { id: 'analytics', label: 'Счётчики и коды', icon: Gauge },
        ],
    },
];
const SECTIONS = SECTION_GROUPS.flatMap((group) => group.items);
const METRIKA_ID_PATTERN = /^\d{5,20}$/;
const GA4_ID_PATTERN = /^G-[A-Z0-9]{4,32}$/i;
const HOSTNAME_PATTERN = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)*[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;
const META_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9_.:-]{0,63}$/;
const RESERVED_META_NAMES = new Set(['google-site-verification', 'yandex-verification']);

const SEO_PAGE_ORDER = [
    'telegram-stars',
    'telegram-stars-price',
    'telegram-stars-russia',
    'telegram-stars-gift',
    'telegram-stars-ton-proof',
    'telegram-stars-not-received',
    'telegram-premium',
    'apple-id',
    'icloud',
    'steam',
    'playstation',
];

const STATIC_SOCIAL_PAGES = [
    {
        key: 'catalog',
        label: 'Каталог цифровых товаров',
        path: '/catalog',
        title: 'Каталог цифровых товаров | Starspay',
        description: 'Apple ID, iCloud+, подписки, игры и коды в отдельном каталоге Starspay.',
    },
    {
        key: 'contacts',
        label: 'Контакты',
        path: '/contacts',
        title: 'Контакты и реквизиты | Starspay',
        description: 'Контакты поддержки и юридические реквизиты сервиса Starspay.',
    },
    {
        key: 'offer',
        label: 'Публичная оферта',
        path: '/offer',
        title: 'Публичная оферта | Starspay',
        description: 'Условия оформления и исполнения заказов в Starspay.',
    },
    {
        key: 'payment-and-delivery',
        label: 'Оплата и доставка',
        path: '/payment-and-delivery',
        title: 'Оплата и доставка | Starspay',
        description: 'Способы оплаты, сроки и порядок выдачи цифровых товаров.',
    },
    {
        key: 'privacy',
        label: 'Конфиденциальность',
        path: '/privacy',
        title: 'Политика конфиденциальности | Starspay',
        description: 'Какие данные обрабатывает Starspay при оформлении и исполнении заказов.',
    },
    {
        key: 'refund-policy',
        label: 'Возвраты',
        path: '/refund-policy',
        title: 'Политика возвратов | Starspay',
        description: 'Условия возврата средств и разбора спорных ситуаций.',
    },
];

function resolveAdminImageSrc(value) {
    return value?.startsWith('/')
        ? `${API_BASE_URL.replace(/\/$/, '')}${value}`
        : value;
}

function Field({
    label,
    value,
    onChange,
    multiline,
    rows = 3,
    type = 'text',
    step,
    min,
    readOnly = false,
    hint,
    error,
}) {
    const className = `mt-1 w-full border rounded-lg px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 ${
        error
            ? 'border-red-300 focus:border-red-400 focus:ring-red-500/20'
            : 'border-slate-200 focus:border-indigo-400 focus:ring-indigo-500/20'
    } ${
        readOnly ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : ''
    }`;
    return (
        <label className="flex min-w-0 flex-col">
            <span className="flex min-h-[40px] items-end pb-1 text-sm leading-5 text-slate-600">{label}</span>
            {multiline ? (
                <textarea
                    className={className}
                    rows={rows}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    readOnly={readOnly}
                    spellCheck
                />
            ) : (
                <input
                    className={`${className} h-11`}
                    type={type}
                    step={step}
                    min={min}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    readOnly={readOnly}
                />
            )}
            {error ? (
                <span className="mt-1.5 flex items-center gap-1 text-xs text-red-600">
                    <AlertCircle size={13} />
                    {error}
                </span>
            ) : (
                hint && <span className="mt-1.5 block text-xs leading-5 text-slate-500">{hint}</span>
            )}
        </label>
    );
}

function Panel({ title, children, actions }) {
    return (
        <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
            <div className="flex items-center justify-between gap-3">
                <h2 className="text-base font-semibold text-slate-900">{title}</h2>
                {actions}
            </div>
            {children}
        </section>
    );
}

function EditableBlock({ title, description, preview, children, open, onToggle }) {
    return (
        <section className={`overflow-hidden rounded-lg border bg-white transition-colors ${open ? 'border-indigo-300' : 'border-slate-200'}`}>
            <button
                type="button"
                onClick={onToggle}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left hover:bg-slate-50"
                aria-expanded={open}
            >
                <span className="min-w-0">
                    <span className="block text-base font-semibold text-slate-900">{title}</span>
                    {description && <span className="mt-1 block text-sm leading-5 text-slate-500">{description}</span>}
                </span>
                <span className={`inline-flex shrink-0 items-center gap-2 rounded-md px-3 py-2 text-sm font-medium ${open ? 'bg-indigo-50 text-indigo-700' : 'border border-slate-200 text-slate-700'}`}>
                    <Pencil size={15} />
                    {open ? 'Закрыть' : 'Редактировать'}
                    {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                </span>
            </button>
            <div className="border-t border-slate-100 bg-slate-50/70 px-5 py-5">{preview}</div>
            {open && <div className="border-t border-slate-200 px-5 py-5">{children}</div>}
        </section>
    );
}

function SocialSharePreview({ image, title, description }) {
    return (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <div className="aspect-[1200/630] bg-slate-100">
                {image ? (
                    <img src={image} alt="Превью страницы для социальных сетей" className="h-full w-full object-cover" />
                ) : (
                    <div className="flex h-full items-center justify-center gap-3 text-slate-400">
                        <ImagePlus size={28} />
                        <span className="text-sm font-medium">Будет использовано стандартное изображение Starspay</span>
                    </div>
                )}
            </div>
            <div className="border-t border-slate-200 px-4 py-3">
                <p className="text-[11px] font-medium uppercase text-slate-400">tgstars.tech</p>
                <p className="mt-1 line-clamp-2 text-sm font-semibold leading-5 text-slate-900">{title || 'Заголовок страницы'}</p>
                <p className="mt-1 line-clamp-2 text-xs leading-5 text-slate-500">{description || 'Описание страницы для социальных сетей'}</p>
            </div>
        </div>
    );
}

function SocialImageField({ value, title, description, onChange, onShowToast }) {
    const inputId = useId();
    const [uploading, setUploading] = useState(false);
    const imageSrc = resolveAdminImageSrc(value);

    const uploadImage = async (file) => {
        if (!file) return;
        if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
            onShowToast?.('Выберите PNG, JPEG или WebP');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            onShowToast?.('Изображение должно быть не больше 5 МБ');
            return;
        }

        setUploading(true);
        try {
            const body = new FormData();
            body.append('file', file);
            const response = await fetch(`${API_BASE_URL}/api/site/content/media`, {
                method: 'POST',
                credentials: 'include',
                body,
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok || !data.path) {
                throw new Error(data.detail || 'Не удалось загрузить изображение');
            }
            onChange(data.path);
            onShowToast?.('Изображение загружено. Сохраните изменения страницы');
        } catch (error) {
            onShowToast?.(error.message || 'Ошибка загрузки изображения');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(280px,0.8fr)]">
            <div className="space-y-4">
                <div>
                    <h3 className="text-sm font-semibold text-slate-900">Изображение для социальных сетей</h3>
                    <p className="mt-1 text-sm leading-6 text-slate-500">
                        Рекомендуемый размер 1200 × 630 px. Форматы PNG, JPEG или WebP, до 5 МБ.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <label
                        htmlFor={inputId}
                        className="inline-flex cursor-pointer items-center gap-2 rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700"
                    >
                        <Upload size={16} />
                        {uploading ? 'Загрузка...' : value ? 'Заменить изображение' : 'Загрузить изображение'}
                    </label>
                    <input
                        id={inputId}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        className="sr-only"
                        disabled={uploading}
                        onChange={(event) => {
                            uploadImage(event.target.files?.[0]);
                            event.target.value = '';
                        }}
                    />
                    {value && (
                        <button
                            type="button"
                            onClick={() => onChange('')}
                            className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                        >
                            <X size={16} />
                            Использовать стандартное
                        </button>
                    )}
                </div>
                <p className="text-xs leading-5 text-slate-500">
                    Картинка используется в Telegram, VK и других сервисах при отправке ссылки на эту страницу.
                </p>
            </div>
            <SocialSharePreview image={imageSrc} title={title} description={description} />
        </div>
    );
}

function Toggle({ checked, onChange, label, description }) {
    return (
        <div className="flex items-start justify-between gap-4">
            <div>
                <p className="text-sm font-medium text-slate-900">{label}</p>
                {description && <p className="mt-1 text-xs leading-5 text-slate-500">{description}</p>}
            </div>
            <button
                type="button"
                role="switch"
                aria-checked={checked}
                aria-label={label}
                onClick={() => onChange(!checked)}
                className={`relative h-6 w-11 shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500/30 ${
                    checked ? 'bg-indigo-600' : 'bg-slate-300'
                }`}
            >
                <span
                    className={`absolute left-0 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
                        checked ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                />
            </button>
        </div>
    );
}

const SiteContentEditor = ({ onShowToast }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [content, setContent] = useState(null);
    const [savedContent, setSavedContent] = useState(null);
    const [activeSection, setActiveSection] = useState('seoPages');
    const [activeSeoPageSlug, setActiveSeoPageSlug] = useState('telegram-stars');
    const [openContentBlock, setOpenContentBlock] = useState('hero');
    const [openSeoBlock, setOpenSeoBlock] = useState('metadata');
    const [activeStaticSocialPage, setActiveStaticSocialPage] = useState('catalog');
    const [isEditorNavCollapsed, setIsEditorNavCollapsed] = useState(() => {
        try {
            return window.localStorage.getItem('site-editor-nav-collapsed') === '1';
        } catch (_) {
            return false;
        }
    });
    const [botCatalogItems, setBotCatalogItems] = useState([]);
    const [botCatalogLoading, setBotCatalogLoading] = useState(false);
    const [savingWebMarkups, setSavingWebMarkups] = useState(false);
    const [showJson, setShowJson] = useState(false);
    const [jsonText, setJsonText] = useState('');
    const editorTopRef = useRef(null);

    const hasChanges = useMemo(() => {
        if (!content || !savedContent) return false;
        return JSON.stringify(content) !== JSON.stringify(savedContent);
    }, [content, savedContent]);

    const toggleEditorNav = () => {
        setIsEditorNavCollapsed((current) => {
            const next = !current;
            try {
                window.localStorage.setItem('site-editor-nav-collapsed', next ? '1' : '0');
            } catch (_) {}
            return next;
        });
    };
    const hasEditorChanges = useMemo(() => {
        if (!showJson) return hasChanges;
        return jsonText !== JSON.stringify(savedContent, null, 2);
    }, [hasChanges, jsonText, savedContent, showJson]);

    const calcClientPrice = (minPrice, markupPercent) => {
        const minP = Number(minPrice || 0);
        let client = minP * (1 + Number(markupPercent || 0) / 100);
        const maxAmt = Math.round(minP * 1.5 * 100) / 100;
        if (client > maxAmt) client = maxAmt;
        return Math.round(client * 100) / 100;
    };

    const loadContent = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/site/content`, { credentials: 'include' });
            if (!res.ok) throw new Error('load failed');
            const data = await res.json();
            const next = data.content ?? {};
            setContent(next);
            setSavedContent(next);
            setJsonText(JSON.stringify(next, null, 2));
        } catch (e) {
            console.error(e);
            onShowToast?.('Не удалось загрузить контент сайта');
        } finally {
            setLoading(false);
        }
    }, [onShowToast]);

    const loadBotCatalogItems = useCallback(async () => {
        setBotCatalogLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/settings/digital-goods/bot-items`, {
                credentials: 'include',
            });
            if (!res.ok) throw new Error('bot items load failed');
            const data = await res.json();
            setBotCatalogItems(
                (data.items || []).map((item) => ({
                    ...item,
                    web_markup_percent:
                        item.web_markup_percent != null
                            ? Number(item.web_markup_percent)
                            : Number(item.markup_percent || 0),
                }))
            );
        } catch (e) {
            console.error(e);
            setBotCatalogItems([]);
            onShowToast?.('Не удалось загрузить товары для сайта');
        } finally {
            setBotCatalogLoading(false);
        }
    }, [onShowToast]);

    useEffect(() => {
        loadContent();
    }, [loadContent]);

    useEffect(() => {
        if (activeSection === 'digitalGoods') {
            loadBotCatalogItems();
        }
    }, [activeSection, loadBotCatalogItems]);

    const setField = (path, value) => {
        setContent((current) => (current ? updateAtPath(current, path, value) : current));
    };

    const setWebMarkup = (itemId, value) => {
        setBotCatalogItems((current) =>
            current.map((item) =>
                item.item_id === itemId
                    ? { ...item, web_markup_percent: Number(value) || 0 }
                    : item
            )
        );
    };

    const saveWebMarkups = async () => {
        setSavingWebMarkups(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/settings/digital-goods/bot-items/web-markups`, {
                method: 'PUT',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    items: botCatalogItems.map((item) => ({
                        item_id: item.item_id,
                        web_markup_percent: Number(item.web_markup_percent || 0),
                    })),
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.detail || 'Не удалось сохранить веб-наценки');
            setBotCatalogItems(
                (data.items || []).map((item) => ({
                    ...item,
                    web_markup_percent:
                        item.web_markup_percent != null
                            ? Number(item.web_markup_percent)
                            : Number(item.markup_percent || 0),
                }))
            );
            onShowToast?.('Веб-наценки сохранены');
        } catch (e) {
            console.error(e);
            onShowToast?.(e.message || 'Ошибка сохранения веб-наценок');
        } finally {
            setSavingWebMarkups(false);
        }
    };

    const setNumberField = (path, value) => {
        const normalized = Number(String(value).replace(',', '.'));
        setField(path, Number.isFinite(normalized) ? normalized : 0);
    };

    const appendItem = (path, item) => {
        setContent((current) => (current ? appendAtPath(current, path, item) : current));
    };

    const removeItem = (path, index) => {
        setContent((current) => (current ? removeAtPath(current, path, index) : current));
    };

    const renderTextField = (label, path, options = {}) => {
        if (!content) return null;
        return (
            <Field
                label={label}
                value={readString(content, path)}
                multiline={options.multiline}
                rows={options.rows}
                type={options.type}
                hint={options.hint}
                error={options.error}
                onChange={(value) => setField(path, value)}
            />
        );
    };

    const renderNumberField = (label, path) => {
        if (!content) return null;
        return (
            <Field
                label={label}
                type="number"
                step="0.01"
                min="0"
                value={String(readNumber(content, path))}
                onChange={(value) => setNumberField(path, value)}
            />
        );
    };

    const renderStringListField = (label, path, options = {}) => {
        if (!content) return null;
        const mode = options.mode || 'lines';
        const separator = mode === 'paragraphs' ? '\n\n' : '\n';
        return (
            <Field
                label={label}
                value={stringListToText(readStringList(content, path), separator)}
                multiline
                rows={options.rows || 5}
                hint={options.hint}
                onChange={(value) => setField(path, textToStringList(value, mode))}
            />
        );
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const payload = showJson ? JSON.parse(jsonText) : content;
            const res = await fetch(`${API_BASE_URL}/api/site/content`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ content: payload }),
            });
            if (!res.ok) throw new Error('save failed');
            const data = await res.json();
            const next = data.content ?? payload;
            setContent(next);
            setSavedContent(next);
            setJsonText(JSON.stringify(next, null, 2));
            onShowToast?.('Контент сайта сохранён');
        } catch (e) {
            console.error(e);
            onShowToast?.(showJson ? 'Ошибка: проверьте JSON' : 'Ошибка сохранения контента');
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        if (savedContent) {
            setContent(JSON.parse(JSON.stringify(savedContent)));
            setJsonText(JSON.stringify(savedContent, null, 2));
            onShowToast?.('Изменения отменены');
        }
    };

    const handleToggleEditor = () => {
        if (!showJson) {
            setJsonText(JSON.stringify(content, null, 2));
            setShowJson(true);
            return;
        }

        try {
            const parsed = JSON.parse(jsonText);
            setContent(parsed);
            setShowJson(false);
        } catch (e) {
            console.error(e);
            onShowToast?.('Ошибка: проверьте JSON перед возвратом в редактор');
        }
    };

    const selectSection = (sectionId) => {
        setActiveSection(sectionId);
        window.requestAnimationFrame(() => {
            const scrollContainer = editorTopRef.current?.closest('.overflow-y-auto');
            if (scrollContainer) {
                scrollContainer.scrollTo({ top: 0, behavior: 'auto' });
                return;
            }
            window.scrollTo({ top: 0, behavior: 'auto' });
        });
    };

    const toggleContentBlock = (blockId) => {
        setOpenContentBlock((current) => (current === blockId ? '' : blockId));
    };

    const toggleSeoBlock = (blockId) => {
        setOpenSeoBlock((current) => (current === blockId ? '' : blockId));
    };

    const renderSection = () => {
        if (!content) return null;

        if (activeSection === 'stats') {
            return (
                <div className="space-y-4">
                    <Panel title="Показатели на главной">
                        <p className="text-sm leading-6 text-slate-600">
                            Четыре карточки показываются сеткой слева. Значение и подпись каждой карточки можно
                            менять независимо.
                        </p>
                        <div className="grid gap-4 lg:grid-cols-2">
                            {(content.stats || []).map((item, index) => (
                                <div key={item.id || index} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                                    <div className="mb-3 flex items-center justify-between gap-3">
                                        <strong className="text-sm text-slate-900">Карточка {index + 1}</strong>
                                        <span className="text-xs text-slate-500">{item.id}</span>
                                    </div>
                                    <div className="grid gap-3 md:grid-cols-2">
                                        {renderTextField('Значение', ['stats', index, 'value'])}
                                        {renderTextField('Подпись', ['stats', index, 'label'])}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Panel>
                    <Panel title="Информационный блок справа">
                        <p className="text-sm leading-6 text-slate-600">
                            Этот текст расположен рядом с показателями. Здесь можно обновить формулировки без правки
                            кода сайта.
                        </p>
                        <div className="grid gap-4 md:grid-cols-2">
                            {renderTextField('Заголовок', ['statsProof', 'title'])}
                            {renderTextField('Подзаголовок', ['statsProof', 'subtitle'])}
                        </div>
                        {renderTextField('Статус', ['statsProof', 'status'])}
                        {renderTextField('Основной текст', ['statsProof', 'body'], { multiline: true, rows: 4 })}
                        {renderTextField('Примечание', ['statsProof', 'note'], { multiline: true, rows: 4 })}
                    </Panel>
                </div>
            );
        }

        const renderHomePageContent = (homePage) => {
            const homePagePath = ['seoPages', 'telegram-stars'];
            return (
                <div className="space-y-4">
                    <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm leading-6 text-blue-900">
                        SEO-настройки главной находятся выше. Ниже показаны её контентные блоки в порядке
                        отображения на сайте.
                    </div>

                    <EditableBlock
                        title="Шапка и первый экран"
                        description="Бренд, главный заголовок и кнопки"
                        open={openContentBlock === 'hero'}
                        onToggle={() => toggleContentBlock('hero')}
                        preview={(
                            <div className="max-w-3xl py-2">
                                <div className="mb-5 flex items-center justify-between border-b border-slate-200 pb-3">
                                    <strong className="text-sm text-slate-900">{content.header?.brandName}</strong>
                                    <span className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white">{content.header?.ctaLabel}</span>
                                </div>
                                <p className="text-xs font-semibold uppercase text-sky-600">{content.hero?.kicker}</p>
                                <h2 className="mt-2 text-2xl font-bold leading-8 text-slate-950">{homePage?.title}</h2>
                                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">{homePage?.lead}</p>
                                <div className="mt-4 flex flex-wrap gap-2">
                                    <span className="rounded-md bg-sky-500 px-4 py-2 text-sm font-semibold text-white">{content.hero?.primaryCta}</span>
                                    <span className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700">{content.hero?.secondaryCta}</span>
                                </div>
                            </div>
                        )}
                    >
                        <div className="space-y-5">
                            <div className="grid gap-4 md:grid-cols-2">
                                {renderTextField('Название бренда', ['header', 'brandName'])}
                                {renderTextField('Метка бренда', ['header', 'brandTag'])}
                                {renderTextField('Кнопка в шапке', ['header', 'ctaLabel'])}
                                {renderTextField('Ссылка кнопки в шапке', ['header', 'ctaHref'])}
                            </div>
                            <div className="border-t border-slate-100 pt-5 space-y-4">
                                <div className="grid gap-4 md:grid-cols-2">
                                    {renderTextField('Надзаголовок', ['hero', 'kicker'])}
                                    {renderTextField('Подпись цены', ['hero', 'rateLabel'])}
                                </div>
                                {renderTextField('H1 главной', [...homePagePath, 'title'])}
                                {renderTextField('Вводный текст', [...homePagePath, 'lead'], { multiline: true, rows: 4 })}
                                <div className="grid gap-4 md:grid-cols-2">
                                    {renderTextField('Основная кнопка', ['hero', 'primaryCta'])}
                                    {renderTextField('Ссылка основной кнопки', ['hero', 'primaryHref'])}
                                    {renderTextField('Вторая кнопка', ['hero', 'secondaryCta'])}
                                    {renderTextField('Ссылка второй кнопки', ['hero', 'secondaryHref'])}
                                </div>
                            </div>
                        </div>
                    </EditableBlock>

                    <EditableBlock
                        title="Преимущества"
                        description="Причины выбрать сервис и сводка заказа"
                        open={openContentBlock === 'benefits'}
                        onToggle={() => toggleContentBlock('benefits')}
                        preview={(
                            <div>
                                <h2 className="text-xl font-bold text-slate-950">{content.benefits?.title}</h2>
                                <p className="mt-1 text-sm leading-6 text-slate-600">{content.benefits?.body}</p>
                                <div className="mt-4 grid gap-3 md:grid-cols-3">
                                    {(content.benefits?.reasons || []).slice(0, 3).map((item) => (
                                        <div key={item.id} className="border-l-2 border-sky-400 pl-3">
                                            <strong className="text-sm text-slate-900">{item.title}</strong>
                                            <p className="mt-1 line-clamp-2 text-xs leading-5 text-slate-500">{item.body}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    >
                        <div className="space-y-5">
                            {renderTextField('Заголовок блока', ['benefits', 'title'])}
                            {renderTextField('Описание блока', ['benefits', 'body'], { multiline: true, rows: 3 })}
                            <div className="space-y-3 border-t border-slate-100 pt-5">
                                <h3 className="text-sm font-semibold text-slate-900">Карточки преимуществ</h3>
                                {(content.benefits?.reasons || []).map((item, index) => (
                                    <div key={item.id || index} className="grid gap-3 border-b border-slate-100 pb-4 md:grid-cols-2">
                                        {renderTextField(`Карточка ${index + 1}`, ['benefits', 'reasons', index, 'title'])}
                                        {renderTextField('Описание', ['benefits', 'reasons', index, 'body'], { multiline: true, rows: 3 })}
                                    </div>
                                ))}
                            </div>
                            <div className="space-y-3 border-t border-slate-100 pt-5">
                                <h3 className="text-sm font-semibold text-slate-900">Сводка заказа</h3>
                                {(content.benefits?.featuredSummary || []).map((item, index) => (
                                    <div key={item.id || index} className="grid gap-3 md:grid-cols-2">
                                        {renderTextField('Подпись', ['benefits', 'featuredSummary', index, 'label'])}
                                        {renderTextField('Значение', ['benefits', 'featuredSummary', index, 'value'])}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </EditableBlock>

                    <EditableBlock
                        title="Отзывы"
                        description="Заголовок блока; сами отзывы приходят из раздела «Отзывы»"
                        open={openContentBlock === 'reviews'}
                        onToggle={() => toggleContentBlock('reviews')}
                        preview={(
                            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                                <div>
                                    <p className="text-xs font-semibold uppercase text-sky-600">{content.reviews?.eyebrow}</p>
                                    <h2 className="mt-1 text-xl font-bold text-slate-950">{content.reviews?.title}</h2>
                                    <p className="mt-1 text-sm leading-6 text-slate-600">{content.reviews?.body}</p>
                                </div>
                                <div className="text-left md:text-right">
                                    <strong className="text-2xl text-slate-950">{content.reviews?.score || '5.0'}</strong>
                                    <p className="text-xs text-slate-500">оценка клиентов</p>
                                </div>
                            </div>
                        )}
                    >
                        <div className="space-y-4">
                            <div className="grid gap-4 md:grid-cols-2">
                                {renderTextField('Надзаголовок', ['reviews', 'eyebrow'])}
                                {renderTextField('Заголовок', ['reviews', 'title'])}
                            </div>
                            {renderTextField('Описание', ['reviews', 'body'], { multiline: true, rows: 3 })}
                            <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm leading-6 text-blue-900">
                                Средняя оценка и карточки подгружаются из раздела «Отзывы». Здесь меняется только оформление блока.
                            </div>
                        </div>
                    </EditableBlock>

                    <EditableBlock
                        title="Premium, FAQ и подвал"
                        description="Финальные информационные блоки главной страницы"
                        open={openContentBlock === 'footer'}
                        onToggle={() => toggleContentBlock('footer')}
                        preview={(
                            <div>
                                <h2 className="text-xl font-bold text-slate-950">{content.premium?.title}</h2>
                                <p className="mt-1 text-sm leading-6 text-slate-600">{content.premium?.body}</p>
                                <div className="mt-4 space-y-2">
                                    {(content.faq?.items || []).slice(0, 2).map((item) => (
                                        <div key={item.id} className="flex items-center justify-between border-b border-slate-200 py-2 text-sm font-medium text-slate-800">
                                            {item.question}<ChevronDown size={16} className="text-slate-400" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    >
                        <div className="space-y-5">
                            {renderTextField('Заголовок Premium', ['premium', 'title'])}
                            {renderTextField('Текст Premium', ['premium', 'body'], { multiline: true, rows: 3 })}
                            <div className="grid gap-4 md:grid-cols-2">
                                {renderTextField('Кнопка Premium', ['premium', 'ctaLabel'])}
                                {renderTextField('Ссылка Premium', ['premium', 'ctaHref'])}
                                {renderTextField('Заголовок FAQ', ['faq', 'title'])}
                                {renderTextField('Подпись в подвале', ['footer', 'tagline'])}
                            </div>
                            {renderTextField('Описание FAQ', ['faq', 'body'], { multiline: true, rows: 3 })}
                            <div className="space-y-4 border-t border-slate-100 pt-5">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-sm font-semibold text-slate-900">Вопросы FAQ</h3>
                                    <button type="button" className="inline-flex items-center gap-1 text-sm text-indigo-600" onClick={() => appendItem(['faq', 'items'], createFaqItem())}>
                                        <Plus size={16} /> Добавить
                                    </button>
                                </div>
                                {(content.faq?.items || []).map((item, index) => (
                                    <div key={item.id || index} className="space-y-3 border-b border-slate-100 pb-4">
                                        <div className="flex items-center justify-between">
                                            <strong className="text-sm text-slate-700">Вопрос {index + 1}</strong>
                                            <button type="button" className="text-red-500" aria-label="Удалить вопрос" onClick={() => removeItem(['faq', 'items'], index)}>
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                        {renderTextField('Вопрос', ['faq', 'items', index, 'question'])}
                                        {renderTextField('Ответ', ['faq', 'items', index, 'answer'], { multiline: true, rows: 4 })}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </EditableBlock>
                </div>
            );
        };

        if (activeSection === 'purchase') {
            return (
                <Panel title="Тексты checkout">
                    <div className="grid md:grid-cols-2 gap-4">
                        {renderTextField('Заголовок формы', ['purchase', 'formTitle'])}
                        {renderTextField('Надзаголовок проверки', ['purchase', 'reviewEyebrow'])}
                        {renderTextField('Заголовок проверки', ['purchase', 'reviewTitle'])}
                        {renderTextField('Метка безопасности', ['purchase', 'secureLabel'])}
                        {renderTextField('Вкладка Stars', ['purchase', 'starsTab'])}
                        {renderTextField('Вкладка Premium', ['purchase', 'premiumTab'])}
                        {renderTextField('Лейбл количества', ['purchase', 'starsFieldLabel'])}
                        {renderTextField('Placeholder количества', ['purchase', 'starsPlaceholder'])}
                        {renderTextField('Лейбл Premium', ['purchase', 'premiumFieldLabel'])}
                        {renderTextField('Лейбл username', ['purchase', 'usernameLabel'])}
                        {renderTextField('Placeholder username', ['purchase', 'usernamePlaceholder'])}
                        {renderTextField('Кнопка Stars', ['purchase', 'buyStarsLabel'])}
                        {renderTextField('Кнопка Premium', ['purchase', 'buyPremiumLabel'])}
                        {renderTextField('Кнопка оплаты', ['purchase', 'paymentCta'])}
                        {renderTextField('Лейбл товара', ['purchase', 'productLabel'])}
                        {renderTextField('Лейбл получателя', ['purchase', 'recipientLabel'])}
                        {renderTextField('Лейбл сбора', ['purchase', 'feeLabel'])}
                        {renderTextField('Лейбл итога', ['purchase', 'totalLabel'])}
                        {renderTextField('Назад', ['purchase', 'backLabel'])}
                    </div>
                    {renderTextField('Подсказка username', ['purchase', 'usernameHelp'], { multiline: true, rows: 2 })}
                    {renderTextField('Ошибка username', ['purchase', 'usernameError'], { multiline: true, rows: 2 })}
                </Panel>
            );
        }

        if (activeSection === 'digitalGoods') {
            const catalogDisabled = Boolean(content?.digitalGoods?.catalogDisabled);
            return (
                <div className="space-y-4">
                    <Panel title="Каталог на сайте">
                        <label className="flex items-start gap-3 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                className="mt-1 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                checked={catalogDisabled}
                                onChange={(e) => setField(['digitalGoods', 'catalogDisabled'], e.target.checked)}
                            />
                            <span>
                                <span className="block text-sm font-medium text-slate-900">
                                    Выключить каталог
                                </span>
                                <span className="block text-sm text-slate-500 mt-0.5">
                                    Если галочка стоит,{' '}
                                    <code className="text-xs bg-slate-100 px-1 rounded">/catalog</code> отдаёт 404
                                    (с шапкой и подвалом сайта). Сохраните контент кнопкой выше.
                                </span>
                            </span>
                        </label>
                    </Panel>

                    <Panel title="Блок на главной">
                        <div className="grid md:grid-cols-2 gap-4">
                            {renderTextField('Надзаголовок', ['digitalGoods', 'kicker'])}
                            {renderTextField('Кнопка', ['digitalGoods', 'ctaLabel'])}
                            {renderTextField('Ссылка кнопки', ['digitalGoods', 'ctaHref'])}
                            {renderTextField('Провайдер (подпись)', ['pricing', 'digitalGoodsProviderLabel'])}
                        </div>
                        {renderTextField('Заголовок', ['digitalGoods', 'title'])}
                        {renderTextField('Описание', ['digitalGoods', 'body'], { multiline: true, rows: 4 })}
                        <div className="space-y-3">
                            {(content.digitalGoods?.items || []).map((item, index) => (
                                <div key={item.id || index} className="grid md:grid-cols-2 gap-3 p-3 bg-slate-50 rounded-lg">
                                    {renderTextField('Пункт', ['digitalGoods', 'items', index, 'title'])}
                                    {renderTextField('Описание', ['digitalGoods', 'items', index, 'body'], { multiline: true, rows: 2 })}
                                </div>
                            ))}
                        </div>
                        {renderTextField('SEO заголовок блока', ['digitalGoods', 'seoTitle'])}
                        {renderTextField('SEO текст блока', ['digitalGoods', 'seoBody'], { multiline: true, rows: 4 })}
                    </Panel>

                    <Panel
                        title={`Цены для сайта (${botCatalogItems.length})`}
                        actions={
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    className="inline-flex items-center gap-1 text-sm text-slate-600"
                                    onClick={loadBotCatalogItems}
                                    disabled={botCatalogLoading || savingWebMarkups}
                                >
                                    <RefreshCw size={16} />
                                    Обновить
                                </button>
                                <button
                                    type="button"
                                    className="inline-flex items-center gap-1 text-sm text-indigo-600 disabled:opacity-50"
                                    onClick={saveWebMarkups}
                                    disabled={botCatalogLoading || savingWebMarkups || botCatalogItems.length === 0}
                                >
                                    <Save size={16} />
                                    {savingWebMarkups ? 'Сохранение...' : 'Сохранить наценки веб'}
                                </button>
                            </div>
                        }
                    >
                        <p className="text-sm text-slate-500">
                            Список — товары с галочкой «В боте» из раздела «Цифровые товары». Наценка веб влияет на
                            цены каталога <code className="text-xs bg-slate-100 px-1 rounded">/catalog</code>.
                        </p>
                        {botCatalogLoading ? (
                            <p className="text-sm text-slate-500">Загрузка товаров...</p>
                        ) : botCatalogItems.length === 0 ? (
                            <p className="text-sm text-slate-500">
                                Пока нет товаров с галочкой «В боте». Отметьте их в разделе «Цифровые товары».
                            </p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-slate-500 border-b">
                                            <th className="py-2 pr-3">Товар</th>
                                            <th className="py-2 pr-3">Себестоимость</th>
                                            <th className="py-2 pr-3">Наценка бот %</th>
                                            <th className="py-2 pr-3">Наценка веб %</th>
                                            <th className="py-2">Цена на сайте</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {botCatalogItems.map((item) => {
                                            const minP = Number(item.source_min_price || 0);
                                            const webMarkup = Number(item.web_markup_percent || 0);
                                            const webPrice = calcClientPrice(minP, webMarkup);
                                            return (
                                                <tr key={item.item_id} className="border-b border-slate-100">
                                                    <td className="py-2 pr-3">
                                                        <div className="font-medium text-slate-800">
                                                            {item.title || item.item_id}
                                                        </div>
                                                        <div className="text-xs text-slate-400">{item.item_id}</div>
                                                    </td>
                                                    <td className="py-2 pr-3 whitespace-nowrap">
                                                        {minP.toFixed(2)} ₽
                                                    </td>
                                                    <td className="py-2 pr-3 whitespace-nowrap text-slate-500">
                                                        {Number(item.markup_percent || 0)}%
                                                    </td>
                                                    <td className="py-2 pr-3">
                                                        <input
                                                            type="number"
                                                            step="0.1"
                                                            min="0"
                                                            value={webMarkup}
                                                            onChange={(e) =>
                                                                setWebMarkup(item.item_id, e.target.value)
                                                            }
                                                            className="w-24 px-2 py-1 border border-slate-200 rounded"
                                                        />
                                                    </td>
                                                    <td className="py-2 font-medium whitespace-nowrap">
                                                        {webPrice.toFixed(2)} ₽
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Panel>
                </div>
            );
        }

        if (activeSection === 'seoPages') {
            const seoPages = content.seoPages || {};
            const availableSlugs = SEO_PAGE_ORDER.filter((slug) => seoPages[slug]);
            const selectedSlug = seoPages[activeSeoPageSlug]
                ? activeSeoPageSlug
                : availableSlugs[0];
            const page = selectedSlug ? seoPages[selectedSlug] : null;

            if (!page) {
                return (
                    <Panel title="Страницы сайта">
                        <p className="text-sm text-slate-500">
                            Страницы ещё не добавлены в контент. Обновите страницу после обновления backend.
                        </p>
                    </Panel>
                );
            }

            const pagePath = ['seoPages', selectedSlug];
            const publicHref = page.routePath || (selectedSlug === 'telegram-stars' ? '/' : `/${selectedSlug}`);

            return (
                <div className="space-y-4">
                    <Panel title="Страница сайта">
                        <div className="grid items-end gap-4 md:grid-cols-[minmax(240px,0.8fr)_minmax(0,1.2fr)]">
                            <label className="block">
                                <span className="mb-1 block text-sm text-slate-600">Что редактируем</span>
                                <select
                                    value={selectedSlug}
                                    onChange={(event) => {
                                        setActiveSeoPageSlug(event.target.value);
                                        setOpenSeoBlock('metadata');
                                    }}
                                    className="h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                                >
                                    {availableSlugs.map((slug) => (
                                        <option key={slug} value={slug}>{seoPages[slug].shortTitle || slug}</option>
                                    ))}
                                </select>
                            </label>
                            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5">
                                <p className="text-xs text-slate-500">Адрес страницы</p>
                                <p className="mt-0.5 truncate font-mono text-sm text-slate-800">{publicHref}</p>
                            </div>
                        </div>
                        <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                            Блоки ниже повторяют структуру страницы сверху вниз. Откройте нужный фрагмент кнопкой
                            «Редактировать». Адреса страниц и переходы к оплате защищены от случайного изменения.
                        </div>
                    </Panel>

                    <EditableBlock
                        title="SEO и карточка для соцсетей"
                        description="Поисковый сниппет и изображение ссылки"
                        open={openSeoBlock === 'metadata'}
                        onToggle={() => toggleSeoBlock('metadata')}
                        preview={(
                            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
                                <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
                                    <p className="truncate text-xs text-emerald-700">tgstars.tech{publicHref === '/' ? '' : publicHref}</p>
                                    <p className="mt-1 line-clamp-2 text-base font-medium leading-6 text-blue-700">{page.metaTitle || page.title}</p>
                                    <p className="mt-1 line-clamp-3 text-xs leading-5 text-slate-600">{page.metaDescription}</p>
                                </div>
                                <SocialSharePreview
                                    image={resolveAdminImageSrc(page.socialImage || content.seo?.socialImage)}
                                    title={page.metaTitle || page.title}
                                    description={page.metaDescription}
                                />
                            </div>
                        )}
                    >
                        <div className="space-y-5">
                        {renderTextField('SEO title', [...pagePath, 'metaTitle'], {
                            hint: 'Заголовок вкладки браузера и поискового сниппета',
                        })}
                        {renderTextField('Meta description', [...pagePath, 'metaDescription'], {
                            multiline: true,
                            rows: 3,
                        })}
                        {renderStringListField('Ключевые запросы', [...pagePath, 'keywords'], {
                            rows: 6,
                            hint: 'Один запрос на строку',
                        })}
                        <div className="border-t border-slate-100 pt-5">
                            <SocialImageField
                                value={readString(content, [...pagePath, 'socialImage'])}
                                title={page.metaTitle || page.title}
                                description={page.metaDescription}
                                onChange={(value) => setField([...pagePath, 'socialImage'], value)}
                                onShowToast={onShowToast}
                            />
                        </div>
                        </div>
                    </EditableBlock>

                    {selectedSlug === 'telegram-stars' && renderHomePageContent(page)}

                    {selectedSlug !== 'telegram-stars' && (
                    <EditableBlock
                        title="Первый экран страницы"
                        description="Надзаголовок, H1 и вводный текст"
                        open={openSeoBlock === 'intro'}
                        onToggle={() => toggleSeoBlock('intro')}
                        preview={(
                            <div className="max-w-3xl py-2">
                                <p className="text-xs font-semibold uppercase text-sky-600">{page.eyebrow}</p>
                                <h2 className="mt-2 text-2xl font-bold leading-8 text-slate-950">{page.title}</h2>
                                <p className="mt-2 text-sm leading-6 text-slate-600">{page.lead}</p>
                                <div className="mt-4 flex flex-wrap gap-2">
                                    <span className="rounded-md bg-sky-500 px-4 py-2 text-sm font-semibold text-white">{page.ctaLabel}</span>
                                    <span className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700">{page.secondaryCta}</span>
                                </div>
                            </div>
                        )}
                    >
                        <div className="grid md:grid-cols-2 gap-4">
                            <Field
                                label="Публичный URL"
                                value={publicHref}
                                onChange={() => {}}
                                readOnly
                                hint="Системное поле"
                            />
                            <Field
                                label="Дата публикации"
                                type="date"
                                value={readString(content, [...pagePath, 'publishedAt'])}
                                onChange={() => {}}
                                readOnly
                                hint="Фиксируется при первой публикации"
                            />
                            <Field
                                label="Дата обновления"
                                type="date"
                                value={readString(content, [...pagePath, 'updatedAt'])}
                                onChange={() => {}}
                                readOnly
                                hint="Обновляется автоматически при изменении контента"
                            />
                            {renderTextField('Короткое название', [...pagePath, 'shortTitle'])}
                            {renderTextField('Название в ссылках', [...pagePath, 'navLabel'])}
                            {renderTextField('Надзаголовок', [...pagePath, 'eyebrow'])}
                        </div>
                        {renderTextField('H1 страницы', [...pagePath, 'title'])}
                        {renderTextField('Вводный текст', [...pagePath, 'lead'], {
                            multiline: true,
                            rows: 4,
                        })}
                    </EditableBlock>
                    )}

                    {selectedSlug !== 'telegram-stars' && (
                    <EditableBlock
                        title="Кнопки и варианты"
                        description="Подписи переходов в профильные разделы"
                        open={openSeoBlock === 'actions'}
                        onToggle={() => toggleSeoBlock('actions')}
                        preview={(
                            <div>
                                <div className="flex flex-wrap gap-2">
                                    <span className="rounded-md bg-sky-500 px-4 py-2 text-sm font-semibold text-white">{page.ctaLabel}</span>
                                    <span className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700">{page.secondaryCta}</span>
                                </div>
                                <div className="mt-4 flex flex-wrap gap-2">
                                    {(page.options || []).slice(0, 5).map((option) => (
                                        <span key={option} className="rounded-md bg-white px-2.5 py-1 text-xs text-slate-600 ring-1 ring-slate-200">{option}</span>
                                    ))}
                                </div>
                            </div>
                        )}
                    >
                        <div className="grid md:grid-cols-2 gap-4">
                            {renderTextField('Основная кнопка', [...pagePath, 'ctaLabel'])}
                            <Field
                                label="Куда ведёт основная кнопка"
                                value={page.ctaHref || ''}
                                onChange={() => {}}
                                readOnly
                                hint="Системный маршрут"
                            />
                            {renderTextField('Вторая кнопка', [...pagePath, 'secondaryCta'])}
                            <Field
                                label="Куда ведёт вторая кнопка"
                                value={page.secondaryHref || ''}
                                onChange={() => {}}
                                readOnly
                                hint="Системный маршрут"
                            />
                        </div>
                        {renderStringListField('Варианты в информационной карточке', [...pagePath, 'options'], {
                            rows: 6,
                            hint: 'Один вариант на строку. Это справочный список, не товары для оплаты.',
                        })}
                    </EditableBlock>
                    )}

                    {selectedSlug !== 'telegram-stars' && (
                    <EditableBlock
                        title="Ключевые преимущества"
                        description="Короткие карточки под первым экраном"
                        open={openSeoBlock === 'highlights'}
                        onToggle={() => toggleSeoBlock('highlights')}
                        preview={(
                            <div className="grid gap-3 md:grid-cols-3">
                                {(page.highlights || []).slice(0, 3).map((item, index) => (
                                    <div key={`${selectedSlug}-highlight-preview-${index}`} className="border-l-2 border-sky-400 pl-3">
                                        <strong className="text-sm text-slate-900">{item.title}</strong>
                                        <p className="mt-1 line-clamp-3 text-xs leading-5 text-slate-500">{item.body}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    >
                        <div className="space-y-4">
                            <div className="flex justify-end">
                            <button
                                type="button"
                                className="inline-flex items-center gap-1 text-sm text-indigo-600"
                                onClick={() => appendItem([...pagePath, 'highlights'], createSeoHighlight())}
                            >
                                <Plus size={16} />
                                Добавить
                            </button>
                            </div>
                            {(page.highlights || []).map((item, index) => (
                                <div key={`${selectedSlug}-highlight-${index}`} className="space-y-3 border-b border-slate-100 pb-4">
                                    <div className="flex justify-between items-center">
                                        <strong className="text-sm text-slate-700">Блок {index + 1}</strong>
                                        <button
                                            type="button"
                                            className="text-red-500"
                                            aria-label="Удалить блок"
                                            onClick={() => removeItem([...pagePath, 'highlights'], index)}
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                    {renderTextField('Заголовок', [...pagePath, 'highlights', index, 'title'])}
                                    {renderTextField('Описание', [...pagePath, 'highlights', index, 'body'], {
                                        multiline: true,
                                        rows: 3,
                                    })}
                                </div>
                            ))}
                        </div>
                    </EditableBlock>
                    )}

                    {selectedSlug !== 'telegram-stars' && (
                    <EditableBlock
                        title="Шаги"
                        description="Последовательность действий для пользователя"
                        open={openSeoBlock === 'steps'}
                        onToggle={() => toggleSeoBlock('steps')}
                        preview={(
                            <div className="grid gap-3 md:grid-cols-3">
                                {(page.steps || []).slice(0, 3).map((item, index) => (
                                    <div key={`${selectedSlug}-step-preview-${index}`} className="flex gap-3">
                                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-sky-500 text-xs font-bold text-white">{index + 1}</span>
                                        <div>
                                            <strong className="text-sm text-slate-900">{item.title}</strong>
                                            <p className="mt-1 line-clamp-3 text-xs leading-5 text-slate-500">{item.body}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    >
                        <div className="space-y-4">
                            <div className="flex justify-end">
                            <button
                                type="button"
                                className="inline-flex items-center gap-1 text-sm text-indigo-600"
                                onClick={() => appendItem([...pagePath, 'steps'], createSeoStep())}
                            >
                                <Plus size={16} />
                                Добавить
                            </button>
                            </div>
                            {(page.steps || []).map((item, index) => (
                                <div key={`${selectedSlug}-step-${index}`} className="space-y-3 border-b border-slate-100 pb-4">
                                    <div className="flex justify-between items-center">
                                        <strong className="text-sm text-slate-700">Шаг {index + 1}</strong>
                                        <button
                                            type="button"
                                            className="text-red-500"
                                            aria-label="Удалить шаг"
                                            onClick={() => removeItem([...pagePath, 'steps'], index)}
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                    {renderTextField('Заголовок', [...pagePath, 'steps', index, 'title'])}
                                    {renderTextField('Описание', [...pagePath, 'steps', index, 'body'], {
                                        multiline: true,
                                        rows: 3,
                                    })}
                                </div>
                            ))}
                        </div>
                    </EditableBlock>
                    )}

                    <EditableBlock
                        title="Основной текст страницы"
                        description="Разделы статьи, списки и источники"
                        open={openSeoBlock === 'article'}
                        onToggle={() => toggleSeoBlock('article')}
                        preview={(
                            <div className="space-y-3">
                                {(page.sections || []).slice(0, 3).map((item, index) => (
                                    <div key={`${selectedSlug}-section-preview-${index}`} className="border-b border-slate-200 pb-3 last:border-0 last:pb-0">
                                        <strong className="text-sm text-slate-900">{item.title}</strong>
                                        <p className="mt-1 line-clamp-2 text-xs leading-5 text-slate-500">{item.paragraphs?.[0]}</p>
                                    </div>
                                ))}
                                {(page.sections || []).length > 3 && (
                                    <p className="text-xs font-medium text-slate-500">Ещё разделов: {(page.sections || []).length - 3}</p>
                                )}
                            </div>
                        )}
                    >
                        <div className="space-y-4">
                            <div className="flex justify-end">
                            <button
                                type="button"
                                className="inline-flex items-center gap-1 text-sm text-indigo-600"
                                onClick={() => appendItem([...pagePath, 'sections'], createSeoSection())}
                            >
                                <Plus size={16} />
                                Добавить раздел
                            </button>
                            </div>
                            {(page.sections || []).map((item, index) => (
                                <div key={`${selectedSlug}-section-${index}`} className="space-y-3 border-b border-slate-100 pb-5">
                                    <div className="flex justify-between items-center">
                                        <strong className="text-sm text-slate-700">Раздел {index + 1}</strong>
                                        <button
                                            type="button"
                                            className="text-red-500"
                                            aria-label="Удалить раздел"
                                            onClick={() => removeItem([...pagePath, 'sections'], index)}
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                    {renderTextField('Заголовок', [...pagePath, 'sections', index, 'title'])}
                                    {renderStringListField('Абзацы', [...pagePath, 'sections', index, 'paragraphs'], {
                                        mode: 'paragraphs',
                                        rows: 8,
                                        hint: 'Разделяйте абзацы пустой строкой',
                                    })}
                                    {renderStringListField('Маркированный список', [...pagePath, 'sections', index, 'bullets'], {
                                        rows: 5,
                                        hint: 'Один пункт на строку',
                                    })}
                                    <div className="grid md:grid-cols-2 gap-4">
                                        {renderTextField('Название источника', [...pagePath, 'sections', index, 'source', 'label'])}
                                        {renderTextField('URL источника', [...pagePath, 'sections', index, 'source', 'href'])}
                                    </div>
                                    <p className="text-xs text-slate-500">
                                        Оставьте URL пустым, если источник не нужен. Разрешены только внешние http/https-ссылки.
                                    </p>
                                </div>
                            ))}
                        </div>
                    </EditableBlock>

                    {selectedSlug !== 'telegram-stars' && (
                    <EditableBlock
                        title="FAQ страницы"
                        description="Вопросы и ответы внизу страницы"
                        open={openSeoBlock === 'faq'}
                        onToggle={() => toggleSeoBlock('faq')}
                        preview={(
                            <div className="space-y-1">
                                {(page.faq || []).slice(0, 4).map((item, index) => (
                                    <div key={`${selectedSlug}-faq-preview-${index}`} className="flex items-center justify-between border-b border-slate-200 py-2.5 text-sm font-medium text-slate-800 last:border-0">
                                        {item.question}<ChevronDown size={16} className="shrink-0 text-slate-400" />
                                    </div>
                                ))}
                            </div>
                        )}
                    >
                        <div className="space-y-4">
                            <div className="flex justify-end">
                            <button
                                type="button"
                                className="inline-flex items-center gap-1 text-sm text-indigo-600"
                                onClick={() => appendItem([...pagePath, 'faq'], createSeoFaqItem())}
                            >
                                <Plus size={16} />
                                Добавить вопрос
                            </button>
                            </div>
                            {(page.faq || []).map((item, index) => (
                                <div key={`${selectedSlug}-faq-${index}`} className="space-y-3 border-b border-slate-100 pb-4">
                                    <div className="flex justify-between items-center">
                                        <strong className="text-sm text-slate-700">Вопрос {index + 1}</strong>
                                        <button
                                            type="button"
                                            className="text-red-500"
                                            aria-label="Удалить вопрос"
                                            onClick={() => removeItem([...pagePath, 'faq'], index)}
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                    {renderTextField('Вопрос', [...pagePath, 'faq', index, 'question'])}
                                    {renderTextField('Ответ', [...pagePath, 'faq', index, 'answer'], {
                                        multiline: true,
                                        rows: 4,
                                    })}
                                </div>
                            ))}
                        </div>
                    </EditableBlock>
                    )}
                </div>
            );
        }

        if (activeSection === 'analytics') {
            const yandexId = readString(content, ['analytics', 'yandexMetrika', 'counterId']);
            const googleId = readString(content, ['analytics', 'googleAnalytics', 'measurementId']);
            const allowedHosts = readStringList(content, ['analytics', 'allowedHosts']);
            const invalidHost = allowedHosts.find((host) => !HOSTNAME_PATTERN.test(host));
            const yandexError = yandexId && !METRIKA_ID_PATTERN.test(yandexId)
                ? 'ID Метрики должен содержать только 5–20 цифр'
                : '';
            const googleError = googleId && !GA4_ID_PATTERN.test(googleId)
                ? 'Ожидается Measurement ID в формате G-XXXXXXXXXX'
                : '';

            return (
                <div className="space-y-4">
                    <Panel title="Где работают счётчики">
                        <Field
                            label="Домены сайта"
                            value={stringListToText(allowedHosts)}
                            multiline
                            rows={3}
                            onChange={(value) => setField(['analytics', 'allowedHosts'], textToStringList(value))}
                            hint="Один домен на строку, без https:// и путей. По умолчанию dev и localhost не отправляют данные в боевые счётчики."
                            error={invalidHost ? `Некорректный домен: ${invalidHost}` : ''}
                        />
                    </Panel>

                    <Panel title="Яндекс.Метрика">
                        <Toggle
                            checked={Boolean(content.analytics?.yandexMetrika?.enabled)}
                            onChange={(value) => setField(['analytics', 'yandexMetrika', 'enabled'], value)}
                            label="Собирать статистику в Яндекс.Метрике"
                            description="Счётчик подключается через официальный tag.js только на разрешённых доменах."
                        />
                        <Field
                            label="ID счётчика"
                            value={yandexId}
                            onChange={(value) => setField(['analytics', 'yandexMetrika', 'counterId'], value.replace(/\s/g, ''))}
                            hint="Например: 111852779"
                            error={yandexError}
                        />
                        <Toggle
                            checked={Boolean(content.analytics?.yandexMetrika?.webvisor)}
                            onChange={(value) => setField(['analytics', 'yandexMetrika', 'webvisor'], value)}
                            label="Вебвизор"
                            description="Записывает обезличенные действия посетителя. Параметры заказа и платёжные токены удаляются из URL перед отправкой."
                        />
                    </Panel>

                    <Panel title="Google Analytics 4">
                        <Toggle
                            checked={Boolean(content.analytics?.googleAnalytics?.enabled)}
                            onChange={(value) => setField(['analytics', 'googleAnalytics', 'enabled'], value)}
                            label="Собирать статистику в Google Analytics"
                            description="Используется отдельный поток GA4. Если поле пустое, остаётся совместимость с настройкой CI/CD."
                        />
                        <Field
                            label="Measurement ID"
                            value={googleId}
                            onChange={(value) => setField(['analytics', 'googleAnalytics', 'measurementId'], value.trim().toUpperCase())}
                            hint="Например: G-XXXXXXXXXX"
                            error={googleError}
                        />
                    </Panel>

                    <Panel title="Подтверждение прав на сайт">
                        <div className="grid gap-4 md:grid-cols-2">
                            {renderTextField('Google Search Console', ['analytics', 'verification', 'google'], {
                                hint: 'Только значение content из meta-тега google-site-verification',
                            })}
                            {renderTextField('Яндекс.Вебмастер', ['analytics', 'verification', 'yandex'], {
                                hint: 'Только значение content из meta-тега yandex-verification',
                            })}
                        </div>
                    </Panel>

                    <Panel
                        title="Дополнительные verification meta-теги"
                        actions={
                            <button
                                type="button"
                                className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-700"
                                onClick={() => appendItem(
                                    ['analytics', 'verification', 'additionalMetaTags'],
                                    { name: '', content: '' }
                                )}
                            >
                                <Plus size={16} />
                                Добавить
                            </button>
                        }
                    >
                        <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm leading-6 text-blue-900">
                            <div className="flex items-start gap-2">
                                <ShieldCheck className="mt-0.5 shrink-0" size={17} />
                                <p>
                                    Здесь можно добавить безопасные meta-теги подтверждения других сервисов. Произвольные
                                    script, HTML и код намеренно не выполняются, чтобы через CMS нельзя было внедрить код
                                    на страницы оформления заказа.
                                </p>
                            </div>
                        </div>
                        <div className="space-y-3">
                            {(content.analytics?.verification?.additionalMetaTags || []).map((item, index) => (
                                <div key={`verification-meta-${index}`} className="grid gap-3 rounded-lg border border-slate-200 p-4 md:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)_auto]">
                                    <Field
                                        label="Meta name"
                                        value={readString(content, ['analytics', 'verification', 'additionalMetaTags', index, 'name'])}
                                        onChange={(value) => setField(['analytics', 'verification', 'additionalMetaTags', index, 'name'], value.trim())}
                                        hint="Например: msvalidate.01"
                                        error={item.name && (
                                            !META_NAME_PATTERN.test(item.name)
                                            || RESERVED_META_NAMES.has(item.name.toLowerCase())
                                        ) ? 'Используйте отдельное поле выше или другое имя meta-тега' : ''}
                                    />
                                    {renderTextField('Content', ['analytics', 'verification', 'additionalMetaTags', index, 'content'])}
                                    <button
                                        type="button"
                                        className="self-start rounded-md p-2 text-red-500 hover:bg-red-50"
                                        aria-label="Удалить meta-тег"
                                        onClick={() => removeItem(['analytics', 'verification', 'additionalMetaTags'], index)}
                                    >
                                        <Trash2 size={17} />
                                    </button>
                                </div>
                            ))}
                            {(content.analytics?.verification?.additionalMetaTags || []).length === 0 && (
                                <div className="flex items-center gap-2 rounded-lg border border-dashed border-slate-300 px-4 py-5 text-sm text-slate-500">
                                    <Tags size={17} />
                                    Дополнительных meta-тегов пока нет
                                </div>
                            )}
                        </div>
                    </Panel>
                </div>
            );
        }

        if (activeSection === 'pricing') {
            return (
                <div className="space-y-4">
                    <Panel title="Отображение цен на сайте">
                        <p className="text-sm text-slate-500">
                            Реальные цены checkout Stars настраиваются в разделе «Цены → Цены для веб-сайта».
                            Здесь — подписи и fallback для hero / Premium.
                        </p>
                        <div className="grid md:grid-cols-3 gap-4">
                            {renderNumberField('Цена за Star (витрина)', ['pricing', 'pricePerStar'])}
                            {renderNumberField('Сбор сервиса, %', ['pricing', 'serviceFeePercent'])}
                            {renderNumberField('Наценка Premium, %', ['pricing', 'premiumMarkupPercent'])}
                        </div>
                    </Panel>
                    <Panel title="Premium (fallback-пакеты)">
                        <p className="text-sm text-slate-500 mb-2">
                            На сайте используются планы из API. Эти значения — запасной вариант, если API недоступен.
                        </p>
                        <div className="space-y-3">
                            {(content.pricing?.premiumPlans || []).map((plan, index) => (
                                <div key={plan.id || index} className="grid md:grid-cols-2 gap-3 p-3 bg-slate-50 rounded-lg">
                                    {renderTextField('Период', ['pricing', 'premiumPlans', index, 'label'])}
                                    {renderNumberField('Цена', ['pricing', 'premiumPlans', index, 'price'])}
                                </div>
                            ))}
                        </div>
                    </Panel>
                </div>
            );
        }

        const staticSocialPage = STATIC_SOCIAL_PAGES.find(
            (page) => page.key === activeStaticSocialPage
        ) || STATIC_SOCIAL_PAGES[0];

        return (
            <div className="space-y-4">
                <Panel title="SEO сайта по умолчанию">
                    <p className="text-sm leading-6 text-slate-500">
                        Эти значения используются для служебных и юридических страниц. Для главной и посадочных
                        страниц задавайте отдельные данные в разделе «Страницы сайта».
                    </p>
                    {renderTextField('Заголовок по умолчанию', ['seo', 'siteTitle'])}
                    {renderTextField('Описание по умолчанию', ['seo', 'siteDescription'], { multiline: true, rows: 3 })}
                </Panel>
                <Panel title="Изображение сайта по умолчанию">
                    <SocialImageField
                        value={readString(content, ['seo', 'socialImage'])}
                        title={readString(content, ['seo', 'siteTitle'])}
                        description={readString(content, ['seo', 'siteDescription'])}
                        onChange={(value) => setField(['seo', 'socialImage'], value)}
                        onShowToast={onShowToast}
                    />
                </Panel>
                <Panel title="Изображения отдельных страниц">
                    <p className="text-sm leading-6 text-slate-500">
                        Для главной и SEO-посадочных страниц картинка задаётся в разделе
                        «Страницы сайта». Здесь настраиваются каталог и служебные страницы.
                    </p>
                    <div className="grid items-end gap-4 md:grid-cols-[minmax(240px,0.8fr)_minmax(0,1.2fr)]">
                        <label className="block">
                            <span className="mb-1 block text-sm text-slate-600">Страница</span>
                            <select
                                value={staticSocialPage.key}
                                onChange={(event) => setActiveStaticSocialPage(event.target.value)}
                                className="h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                            >
                                {STATIC_SOCIAL_PAGES.map((page) => (
                                    <option key={page.key} value={page.key}>{page.label}</option>
                                ))}
                            </select>
                        </label>
                        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5">
                            <p className="text-xs text-slate-500">Адрес страницы</p>
                            <p className="mt-0.5 truncate font-mono text-sm text-slate-800">{staticSocialPage.path}</p>
                        </div>
                    </div>
                    <div className="border-t border-slate-100 pt-5">
                        <SocialImageField
                            value={readString(content, ['seo', 'socialImages', staticSocialPage.key])}
                            title={staticSocialPage.title}
                            description={staticSocialPage.description}
                            onChange={(value) => setField(['seo', 'socialImages', staticSocialPage.key], value)}
                            onShowToast={onShowToast}
                        />
                    </div>
                </Panel>
                <Panel title="Структурированные данные Schema.org">
                    {renderTextField('Название страницы', ['seo', 'webPageName'])}
                    {renderTextField('Описание страницы', ['seo', 'webPageDescription'], { multiline: true, rows: 3 })}
                    {renderTextField('Описание организации', ['seo', 'organizationDescription'], { multiline: true, rows: 3 })}
                </Panel>
            </div>
        );
    };

    if (loading) {
        return <div className="text-slate-500">Загрузка контента сайта...</div>;
    }

    return (
        <div className="space-y-5">
            <div ref={editorTopRef} className="sticky top-0 z-20 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white/95 p-4 shadow-sm backdrop-blur">
                <div className="flex items-center gap-2">
                    <FileText className="text-indigo-600" size={22} />
                    <div>
                        <h1 className="text-xl font-bold text-slate-900">Управление сайтом</h1>
                        <p className="text-sm text-slate-500">
                            Контент, SEO и внешние интеграции
                            {hasEditorChanges && <span className="ml-2 font-medium text-amber-600">· есть несохранённые изменения</span>}
                        </p>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        onClick={handleToggleEditor}
                        className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50"
                    >
                        <Code2 size={16} />
                        {showJson ? 'Визуальный редактор' : 'JSON'}
                    </button>
                    <button
                        type="button"
                        onClick={handleReset}
                        disabled={!hasEditorChanges || saving}
                        className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                    >
                        <RefreshCw size={16} />
                        Отменить
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={!hasEditorChanges || saving}
                        className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <Save size={16} />
                        {saving ? 'Сохранение...' : 'Сохранить'}
                    </button>
                </div>
            </div>

            {showJson ? (
                <textarea
                    className="w-full min-h-[520px] font-mono text-sm border border-slate-200 rounded-xl p-4"
                    value={jsonText}
                    onChange={(e) => setJsonText(e.target.value)}
                    spellCheck={false}
                />
            ) : (
                <div className={`grid items-start gap-4 ${isEditorNavCollapsed ? 'xl:grid-cols-[72px_minmax(0,1fr)]' : 'xl:grid-cols-[220px_minmax(0,1fr)]'}`}>
                    <nav className={`grid gap-4 rounded-lg border border-slate-200 bg-white p-3 shadow-sm sm:grid-cols-3 xl:sticky xl:top-24 xl:block ${isEditorNavCollapsed ? 'xl:space-y-2 xl:px-2' : 'xl:space-y-5'}`}>
                        <div className={`hidden xl:flex ${isEditorNavCollapsed ? 'justify-center' : 'items-center justify-between px-2'}`}>
                            {!isEditorNavCollapsed && (
                                <span className="text-xs font-semibold uppercase text-slate-400">Разделы</span>
                            )}
                            <button
                                type="button"
                                onClick={toggleEditorNav}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-indigo-600"
                                title={isEditorNavCollapsed ? 'Развернуть меню редактора' : 'Свернуть меню редактора'}
                                aria-label={isEditorNavCollapsed ? 'Развернуть меню редактора' : 'Свернуть меню редактора'}
                            >
                                {isEditorNavCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
                            </button>
                        </div>
                        {SECTION_GROUPS.map((group) => (
                            <div key={group.label}>
                                <p className={`mb-2 px-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400 ${isEditorNavCollapsed ? 'xl:hidden' : ''}`}>
                                    {group.label}
                                </p>
                                <div className="space-y-1">
                                    {group.items.map(({ id, label, icon: Icon }) => (
                                        <button
                                            key={id}
                                            type="button"
                                            onClick={() => selectSection(id)}
                                            title={isEditorNavCollapsed ? label : undefined}
                                            className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors ${isEditorNavCollapsed ? 'xl:justify-center xl:px-0' : ''} ${
                                                activeSection === id
                                                    ? 'bg-indigo-50 text-indigo-700'
                                                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                                            }`}
                                        >
                                            <Icon size={17} className={activeSection === id ? 'text-indigo-600' : 'text-slate-400'} />
                                            <span className={isEditorNavCollapsed ? 'xl:hidden' : ''}>{label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </nav>
                    <main className="min-w-0">{renderSection()}</main>
                </div>
            )}
        </div>
    );
};

export default SiteContentEditor;
